import { useState, useEffect, useCallback } from 'react'
import { fetchRepositories, createGithubSite } from 'services/manage'
import { useLocation, useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { fetchUserData, setPreviewData, setPreviewModal, showMessageModal } from '../Manage/actions'
import { convertToBytes, pluralize, onUpgradePlan, canSubmitUpload, getTLDs } from 'utils/general'
import { showUpgradeCardModal } from 'actions'
import { uploadedMoreThanOneProject, uploadedFileTooBig } from 'utils/sendy'
import TINY_PLAN from 'constants/plans/tiny'
import { PRO_MAX_FILE_SIZES } from 'constants/plans'
import { MAX_FILE_SIZE, TLDS, MAX_FILE_SIZE_FREE, MAX_REPO_SIZE_MB } from '../constants'

const SUCCESS = 'SUCCESS'
const CONNECTED = 'CONNECTED'
const LOADING = 'LOADING'

const useGitHubRepos = (userProfile, githubData, liveSitesCount, onHide, onUpdate, activeSpace) => {
  const [allRepos, setAllRepos] = useState([])
  const [deploying, setDeploying] = useState('')
  const [pageState, setPageState] = useState(CONNECTED)
  const [files, setFiles] = useState([])
  const [newSite, setNewSite] = useState(undefined)
  const [error, setError] = useState("Oops! We couldn't deploy this repository. Please try again.")
  const [selectedRepo, setSelectedRepo] = useState(null)
  const [resyncing, setResyncing] = useState(false)

  const [showFileList, setShowFileList] = useState(false)
  const [htmlFilesForRepo, setHtmlFilesForRepo] = useState([])
  const [phpFilesForRepo, setPhpFilesForRepo] = useState([])
  const [currentRepoForFiles, setCurrentRepoForFiles] = useState(null)
  const [showingPhpFiles, setShowingPhpFiles] = useState(false)
  const [domainSelections, setDomainSelections] = useState({})

  const location = useLocation()
  const dispatch = useDispatch()
  const history = useHistory()
  const tlds = getTLDs({ account: userProfile })
  const searchParams = new URLSearchParams(location.search)
  const status = searchParams.get('status')
  const canGetRepos = status === 'success'
  const isTrial = !userProfile.productId
  const productId = userProfile.productId
  const siteLimit = userProfile.siteLimit || 1

  const maxSizeMB = isTrial ? MAX_FILE_SIZE_FREE : PRO_MAX_FILE_SIZES[productId] || MAX_FILE_SIZE
  const maxSizeBytes = convertToBytes(maxSizeMB)

  const isRepoSizeExceeded = (repoSizeKB) => {
    const repoSizeBytes = repoSizeKB * 1024
    return repoSizeBytes > maxSizeBytes
  }

  const handleDomainChange = useCallback((repoId, domainData) => {
    setDomainSelections((prev) => ({
      ...prev,
      [repoId]: domainData
    }))
  }, [])

  const handleResyncRepos = async () => {
    if (!githubData?.access_token || !userProfile?.email) return

    setResyncing(true)
    await handleFetchRepos(githubData.access_token, githubData.installation_id, userProfile.email)
    setResyncing(false)
  }

  const executeDeployment = (repo, deployFunction) => {
    if (!validateRepo(repo)) {
      return false
    }

    const domainData = domainSelections[repo.id]
    const hasPhpFiles = repo.rootPhpFiles && repo.rootPhpFiles.length > 0

    if (hasPhpFiles) {
      const hasMultiplePhpFiles = repo.rootPhpFiles.length > 1
      const hasSinglePhpFile = repo.rootPhpFiles.length === 1

      if (hasMultiplePhpFiles && !repo.isPhpSelected) {
        showFileSelector(repo, true)
        return false
      } else if (hasSinglePhpFile && !repo.isPhpSelected) {
        setSelectedIndexFile(repo.full_name, repo.rootPhpFiles[0], true)
        deployFunction(repo.full_name, repo.rootPhpFiles[0], true, domainData)
        return true
      } else if (repo.isPhpSelected) {
        deployFunction(repo.full_name, repo.selectedIndexFile, true, domainData)
        return true
      }
    }

    const hasMultipleHtmlFiles = repo.rootHtmlFiles && repo.rootHtmlFiles.length > 1
    const hasSingleHtmlFile = repo.rootHtmlFiles && repo.rootHtmlFiles.length === 1

    if (hasMultipleHtmlFiles && !repo.selectedIndexFile) {
      showFileSelector(repo, false)
      return false
    } else if (hasSingleHtmlFile) {
      deployFunction(repo.full_name, repo.rootHtmlFiles[0], false, domainData)
      return true
    } else if (repo.selectedIndexFile) {
      deployFunction(repo.full_name, repo.selectedIndexFile, false, domainData)
      return true
    } else {
      deployFunction(repo.full_name, null, false, domainData)
      return true
    }
  }

  const handleRedeploy = (repo) => {
    return executeDeployment(repo, onDeploy)
  }

  const handleFetchRepos = async (token, id, email) => {
    try {
      setError(null)
      setPageState(LOADING)
      const payload = { token, id, email }
      const {
        data: { repos }
      } = await fetchRepositories(payload)

      if (repos) {
        const enhancedRepos = repos.map((repo) => ({
          ...repo,
          selectedIndexFile: null,
          isPhpSelected: false
        }))
        setAllRepos(enhancedRepos)
      }

      setPageState(CONNECTED)
      dispatch(fetchUserData(false))
      history.replace({ pathname: '/manage' })
    } catch (error) {
      setError('Failed to fetch repositories. Please try again.')
      setPageState(CONNECTED)
    }
  }

  const setSelectedIndexFile = (repoFullName, indexFile, isPhp = false) => {
    setAllRepos((prevRepos) =>
      prevRepos.map((repo) => {
        if (repo.full_name === repoFullName) {
          return {
            ...repo,
            selectedIndexFile: indexFile,
            isPhpSelected: isPhp
          }
        }
        return repo
      })
    )

    setCurrentRepoForFiles(null)
    setShowFileList(false)
    setShowingPhpFiles(false)
  }

  const isDeployButtonDisabled = (repo) => {
    if (repo.rootPhpFiles && repo.rootPhpFiles.length > 1 && !repo.selectedIndexFile) {
      return true
    }
    if (!repo.isPhpSelected && repo.rootHtmlFiles && repo.rootHtmlFiles.length > 1 && !repo.selectedIndexFile) {
      return true
    }

    const domainData = domainSelections[repo.id]
    if (domainData && !domainData.isValid) {
      return true
    }

    return false
  }

  const showFileSelector = (repo, isPhp = false) => {
    if (isPhp) {
      setPhpFilesForRepo(repo.rootPhpFiles || [])
      setShowingPhpFiles(true)
    } else {
      setHtmlFilesForRepo(repo.rootHtmlFiles || [])
      setShowingPhpFiles(false)
    }
    setCurrentRepoForFiles(repo)
    setShowFileList(true)
  }

  const handleFileSelected = (file) => {
    if (currentRepoForFiles) {
      setSelectedIndexFile(currentRepoForFiles.full_name, file, showingPhpFiles)
    }
  }

  const handleDeployClick = (repo) => {
    return executeDeployment(repo, onDeploy)
  }

  const onDeploy = async (repoFullName, indexFile = null, isPhp = false, domainData = null) => {
    try {
      setError(null)
      setDeploying(repoFullName)
      const { repos } = githubData

      const repo = repos.find((repo) => repo.full_name === repoFullName)

      const payload = {
        repoName: repoFullName,
        repoId: repo?.id,
        branch: repo?.default_branch,
        indexFile: indexFile
      }

      if (domainData) {
        payload.newDomain = domainData?.subdomain ? domainData.subdomain + domainData?.domainSuffix : null
        payload.domainSuffix = domainData?.domainSuffix
      }

      if (isPhp) {
        payload.domainSuffix = TLDS.TIINY_IO
      }

      if (activeSpace && activeSpace !== 'archive' && activeSpace !== 'all') {
        payload.spaceId = activeSpace
      }

      const { data } = await createGithubSite(payload)
      setDeploying(false)
      if (data?.link) {
        if (data?.link?.inPreviewMode) {
          dispatch(setPreviewData(data.link))
          dispatch(setPreviewModal(true))
          dispatch(fetchUserData(false))
          onClose()
        } else {
          setNewSite(data?.link)
          setFiles(data?.files)
          dispatch(fetchUserData(false))
          setPageState(SUCCESS)
        }
      }
    } catch (error) {
      setError("Oops! We couldn't deploy this repository. Please try again.")
      setDeploying(false)
    }
  }

  const validateRepo = (repo) => {
    const sizeInBytes = repo?.size * 1024

    const repoObject = {
      size: sizeInBytes,
      type: 'application/zip'
    }

    const isMaxSizeExceeded = sizeInBytes > convertToBytes(MAX_REPO_SIZE_MB)
    if (isMaxSizeExceeded) {
      dispatch(
        showMessageModal({
          title: 'Maximum repository size exceeded',
          message: `This feature only allows you to deploy repositories less than ${MAX_REPO_SIZE_MB}MB.`
        })
      )
      return false
    }

    if (isRepoSizeExceeded(repo?.size)) {
      dispatch(
        showUpgradeCardModal({
          title: 'Upgrade to upload larger files',
          desc: `Your current plan only allows you to upload files less than ${maxSizeMB || MAX_FILE_SIZE}MB. Upload more on a different plan.`,
          cta: 'Upload larger files',
          onClick: repoObject?.size ? () => onUpgradePlan({ fileSize: repoObject?.size, userProfile }, dispatch) : null
        })
      )
      uploadedFileTooBig()
      return false
    }

    const containsPhpFiles = repo.rootPhpFiles && repo.rootPhpFiles.length > 0
    if (isTrial && containsPhpFiles) {
      dispatch(
        showUpgradeCardModal({
          title: "PHP files can't be hosted on the free plan",
          desc: `Please upgrade to upload PHP files.`,
          cta: 'Upgrade now',
          onClick: () => onUpgradePlan({ planId: TINY_PLAN.id, userProfile }, dispatch)
        })
      )
      return false
    }

    if (!canSubmitUpload(userProfile)) {
      dispatch(
        showUpgradeCardModal({
          title: "You've reached your daily upload limit",
          desc: `You can only update your project 3 times per day, please upgrade or return tomorrow.`,
          cta: 'Upgrade now',
          onClick: () => onUpgradePlan({ planId: TINY_PLAN.id, userProfile }, dispatch)
        })
      )
      onClose()
      return false
    }

    if (siteLimit <= liveSitesCount) {
      uploadedMoreThanOneProject()
      dispatch(
        showUpgradeCardModal({
          title: 'Upgrade to upload more content',
          desc: `Your current plan only allows ${siteLimit} live ${pluralize(
            'project',
            'projects',
            siteLimit
          )}. Upload more on a different plan.`,
          cta: 'Create more projects',
          onClick: () => onUpgradePlan({ projects: siteLimit + 1, userProfile }, dispatch)
        })
      )
      return false
    }

    return true
  }

  const onReset = (site) => {
    onUpdate(site)
    onHide()
    setPageState(CONNECTED)
  }

  const onClose = () => {
    onHide()
    setPageState(CONNECTED)
    setError(null)
  }

  const dismissError = () => {
    setError(null)
    setDeploying('')
  }

  const toggleRepoDetails = (repo) => {
    if (selectedRepo && selectedRepo.id === repo.id) {
      setSelectedRepo(null)
    } else {
      setSelectedRepo(repo)
    }
  }

  const onOpenUrl = () => {
    const currentUrl = window.location.href
    const state = JSON.stringify({
      redirectUrl: currentUrl,
      metadata: { email: userProfile?.email }
    })
    const path = githubData?.access_token ? 'installations/select_target' : 'installations/new'
    const githubUrl = `https://github.com/apps/tiiny-host-sync/${path}?state=${encodeURIComponent(state)}`
    window.open(githubUrl)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Unknown date'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const renderFilesBadge = (repo) => {
    if (repo.rootPhpFiles && repo.rootPhpFiles.length > 0) {
      return {
        count: repo.rootPhpFiles.length,
        selectedFile: repo.isPhpSelected ? repo.selectedIndexFile : null,
        fileType: 'PHP'
      }
    }

    if (repo.rootHtmlFiles && repo.rootHtmlFiles.length > 1) {
      return {
        count: repo.rootHtmlFiles.length,
        selectedFile: !repo.isPhpSelected ? repo.selectedIndexFile : null,
        fileType: 'HTML'
      }
    }

    return null
  }

  useEffect(() => {
    if (githubData?.repos && !canGetRepos) {
      const enhancedRepos = githubData.repos.map((repo) => ({
        ...repo,
        selectedIndexFile: null,
        isPhpSelected: false
      }))
      setAllRepos(enhancedRepos)
    }
  }, [githubData?.repos])

  useEffect(() => {
    if (canGetRepos && githubData?.access_token && userProfile?.email) {
      handleFetchRepos(githubData?.access_token, githubData?.installation_id, userProfile?.email)
    }
  }, [userProfile?.email, githubData?.access_token])

  return {
    allRepos,
    handleFetchRepos,
    onDeploy,
    deploying,
    pageState,
    newSite,
    files,
    onClose,
    error,
    setError,
    dismissError,
    selectedRepo,
    toggleRepoDetails,
    onOpenUrl,
    formatDate,
    formatDateTime,
    showFileList,
    setShowFileList,
    htmlFilesForRepo,
    phpFilesForRepo,
    showingPhpFiles,
    currentRepoForFiles,
    handleFileSelected,
    isDeployButtonDisabled,
    showFileSelector,
    handleDeployClick,
    renderFilesBadge,
    isRepoSizeExceeded,
    onReset,
    handleDomainChange,
    handleResyncRepos,
    handleRedeploy,
    resyncing,
    tlds
  }
}

export default useGitHubRepos
